import { useCallback, useEffect, useMemo, useState } from 'react'

import type { IntakeFormListItem } from 'features/Procedure/state/procedure-api.type'

type IntakeNavStatus = 'loading' | 'active' | 'complete'

export type IntakeNavState = {
  currentFormId: number | null
  currentIndex: number
  total: number
  canPrev: boolean
  canNext: boolean
  status: IntakeNavStatus
  goPrev: () => void
  goNext: () => void
  handleSigned: (signedId: number) => void
}

export const useIntakeFormsNavigation = (
  forms: IntakeFormListItem[] | undefined,
): IntakeNavState => {
  const [currentFormId, setCurrentFormId] = useState<number | null>(null)
  const [signedIds, setSignedIds] = useState<ReadonlySet<number>>(() => new Set())

  const effective = useMemo(
    () => (forms ?? []).filter((f) => !signedIds.has(f.id)),
    [forms, signedIds],
  )

  useEffect(() => {
    if (forms === undefined) return
    setSignedIds((prev) => {
      if (prev.size === 0) return prev
      const presentIds = new Set(forms.map((f) => f.id))
      const next = new Set([...prev].filter((id) => presentIds.has(id)))
      return next.size === prev.size ? prev : next
    })
  }, [forms])

  useEffect(() => {
    if (forms === undefined) return
    const stillPresent = currentFormId !== null && effective.some((f) => f.id === currentFormId)
    if (stillPresent) return
    setCurrentFormId(effective[0]?.id ?? null)
  }, [forms, effective, currentFormId])

  const currentIndex = effective.findIndex((f) => f.id === currentFormId)
  const total = effective.length

  const goPrev = useCallback(() => {
    if (currentIndex <= 0) return
    setCurrentFormId(effective[currentIndex - 1]?.id ?? null)
  }, [currentIndex, effective])

  const goNext = useCallback(() => {
    if (currentIndex < 0 || currentIndex >= total - 1) return
    setCurrentFormId(effective[currentIndex + 1]?.id ?? null)
  }, [currentIndex, total, effective])

  const handleSigned = useCallback(
    (signedId: number) => {
      setCurrentFormId((id) => {
        if (id !== signedId) return id
        const idx = effective.findIndex((f) => f.id === signedId)
        return effective[idx + 1]?.id ?? effective[idx - 1]?.id ?? null
      })
      setSignedIds((prev) => {
        const next = new Set(prev)
        next.add(signedId)
        return next
      })
    },
    [effective],
  )

  const status: IntakeNavStatus =
    forms === undefined ? 'loading' : effective.length > 0 ? 'active' : 'complete'

  return {
    currentFormId,
    currentIndex,
    total,
    canPrev: currentIndex > 0,
    canNext: currentIndex >= 0 && currentIndex < total - 1,
    status,
    goPrev,
    goNext,
    handleSigned,
  }
}
